import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Landmark, Link as LinkIcon, TrendingUp, TrendingDown, ChevronDown } from "lucide-react";
import { useDisplay } from "@/context/DisplayContext";

function fmt(v, ccy = "GBP") {
  const n = Number(v || 0);
  const symbol = ccy === "GBP" ? "£" : ccy === "USD" ? "$" : ccy === "EUR" ? "€" : "";
  return `${n < 0 ? "-" : ""}${symbol}${Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function timeAgo(iso) {
  if (!iso) return "Never";
  try {
    const now = new Date();
    const d = new Date(iso);
    const s = Math.floor((now - d) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return iso; }
}

/**
 * Dashboard card: live bank balances from TrueLayer + Net Position (balance − total debt).
 */
export default function BankBalanceCard({ api, totalDebt = 0 }) {
  const [data, setData] = useState(null); // { connected, accounts, total_available, ... }
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Collapse/expand toggle — same persistence as other dashboard sections.
  // Must be called BEFORE any early return so hook order stays stable.
  const { toggleSection, isSectionCollapsed } = useDisplay();
  const collapsed = isSectionCollapsed("dash-bank-balance");

  const fetchBalances = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${api}/auth/truelayer/balances`, { params: isRefresh ? { refresh: true } : {} });
      setData(res.data);
    } catch (e) {
      setData({ connected: false, accounts: [], total_available: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="bank-balance-card-loading">
        <p className="text-xs text-gray-400">Loading bank balances…</p>
      </div>
    );
  }

  // No bank connected — prompt to connect
  if (!data?.connected) {
    return (
      <div
        data-testid="bank-balance-connect-prompt"
        className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-sm p-4 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <Landmark className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-semibold text-gray-900">Connect a bank account</p>
            <p className="text-xs text-gray-600 mt-0.5">
              See your live balance & net position. UK Open Banking via TrueLayer.
            </p>
          </div>
        </div>
        <a
          href="#settings"
          onClick={(e) => {
            e.preventDefault();
            // Trigger the parent app's state-based routing. Fallback: push hash.
            window.dispatchEvent(new CustomEvent("navigate", { detail: { page: "settings" } }));
            const link = document.querySelector('[data-sidebar-item="settings"]');
            if (link) link.click();
          }}
          className="text-xs bg-black text-white rounded-sm px-3 py-1.5 hover:bg-gray-800 flex items-center gap-1.5"
          data-testid="dashboard-connect-bank-link"
        >
          <LinkIcon className="w-3 h-3" strokeWidth={1.5} />
          Connect Bank
        </a>
      </div>
    );
  }

  // Connected — show balance breakdown
  const accounts = data.accounts || [];
  const bankAccounts = accounts.filter((a) => a.kind === "account");
  const cards = accounts.filter((a) => a.kind === "card");
  const currency = accounts[0]?.currency || "GBP";
  const totalAvailable = data.total_available || 0;
  const netPosition = totalAvailable - (totalDebt || 0);
  const netIsPositive = netPosition >= 0;

  return (
    <div data-testid="bank-balance-card" className="bg-white border border-gray-200 rounded-sm overflow-hidden">
      {/* Header — entire bar is clickable, matching CollapsibleSection */}
      <button
        type="button"
        onClick={() => toggleSection("dash-bank-balance")}
        data-testid="toggle-bank-balance"
        className="w-full border-b border-gray-100 px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors"
        style={{ borderBottom: collapsed ? "none" : "1px solid #f3f4f6" }}
      >
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
          strokeWidth={1.5}
        />
        <Landmark className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
        <h2 className="font-heading text-xs font-semibold uppercase tracking-wider text-gray-600">Bank Balance</h2>
        <span className="text-[10px] text-gray-400 ml-auto" data-testid="balance-refreshed-at">
          Refreshed: {timeAgo(data.refreshed_at)}
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); fetchBalances(true); }}
          role="button" tabIndex={0}
          className="p-1 hover:bg-gray-100 rounded-sm"
          title="Refresh balances"
          data-testid="refresh-balances-btn"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 text-gray-500 ${refreshing ? "animate-spin" : ""}`}
            strokeWidth={1.5}
          />
        </span>
      </button>

      {!collapsed && (
      <>
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Available</p>
          <p className="text-xl sm:text-2xl font-heading font-semibold text-gray-900 mt-1 font-mono-data" data-testid="total-available-balance">
            {fmt(totalAvailable, currency)}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {bankAccounts.length} {bankAccounts.length === 1 ? "account" : "accounts"}
          </p>
        </div>
        <div className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Debt</p>
          <p className="text-xl sm:text-2xl font-heading font-semibold text-red-700 mt-1 font-mono-data" data-testid="total-debt-balance">
            {fmt(-(totalDebt || 0), currency)}
          </p>
        </div>
        <div className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
            Net Position
            {netIsPositive ? (
              <TrendingUp className="w-3 h-3 text-green-600" strokeWidth={2} />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-600" strokeWidth={2} />
            )}
          </p>
          <p
            className={`text-xl sm:text-2xl font-heading font-semibold mt-1 font-mono-data ${netIsPositive ? "text-green-700" : "text-red-700"}`}
            data-testid="net-position-balance"
          >
            {fmt(netPosition, currency)}
          </p>
        </div>
      </div>

      {/* Per-account breakdown */}
      {accounts.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Accounts</p>
          <div className="space-y-1.5" data-testid="balance-accounts-list">
            {bankAccounts.map((a) => (
              <div
                key={a.account_id}
                className="flex items-center justify-between text-xs border border-gray-100 rounded-sm px-2.5 py-1.5 hover:bg-gray-50"
                data-testid={`balance-account-${a.account_id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm">🏦</span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {a.bank_name} · {a.name}
                    </p>
                    <p className="text-[10px] text-gray-400">{a.type}</p>
                  </div>
                </div>
                <span className="font-mono-data font-semibold text-gray-900 tabular-nums shrink-0">
                  {fmt(a.available, a.currency)}
                </span>
              </div>
            ))}
            {cards.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-3 mb-1">Credit Cards</p>
                {cards.map((c) => (
                  <div
                    key={c.account_id}
                    className="flex items-center justify-between text-xs border border-gray-100 rounded-sm px-2.5 py-1.5 hover:bg-gray-50"
                    data-testid={`balance-card-${c.account_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-sm">💳</span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {c.bank_name} · {c.name}
                        </p>
                        <p className="text-[10px] text-gray-400">Owed: {fmt(c.current, c.currency)}</p>
                      </div>
                    </div>
                    <span className="font-mono-data text-gray-600 tabular-nums shrink-0">
                      Avail: {fmt(c.available, c.currency)}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {data.errors && data.errors.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-[10px] text-amber-800">
          {data.errors.length} issue(s): {data.errors[0]}
        </div>
      )}
      </>
      )}
    </div>
  );
}
